import { useState, useEffect } from "react";
import axios from "axios";
import { ArrowDownLeft, ArrowUpRight, TrendingUp, Loader2, Upload, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

import { API } from "@/apiConfig";

export default function WalletPage({ profile, onRefresh, initialSection }) {
  const [activeSection, setActiveSection] = useState(initialSection || "overview");
  const [analytics, setAnalytics] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [depositAmount, setDepositAmount] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [settings, setSettings] = useState({ min_deposit: 200, min_withdrawal: 500 });

  // Deposit flow states
  const [depositStep, setDepositStep] = useState("amount"); // amount -> method -> receipt
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [pendingTxnId, setPendingTxnId] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);

  // Withdraw form
  const [bankName, setBankName] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [upiId, setUpiId] = useState("");

  useEffect(() => {
    fetchData();
    fetchBankCard();
  }, []);

  useEffect(() => {
    if (initialSection) setActiveSection(initialSection);
  }, [initialSection]);

  const fetchBankCard = async () => {
    try {
      const { data } = await axios.get(`${API}/user/bank-card`);
      if (data && data.bank_account_name) {
        setBankName(data.bank_name || "");
        setAccountName(data.bank_account_name || "");
        setAccountNumber(data.bank_account_number || "");
        setIfsc(data.bank_ifsc || "");
        setUpiId(data.upi_id || "");
      }
    } catch { }
  };

  const fetchData = async () => {
    try {
      const [analyticsRes, txnRes, methodsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/wallet/analytics`),
        axios.get(`${API}/wallet/transactions`),
        axios.get(`${API}/wallet/payment-methods`),
        axios.get(`${API}/wallet/settings`),
      ]);
      setAnalytics(analyticsRes.data);
      setTransactions(txnRes.data);
      setPaymentMethods(methodsRes.data.filter(m => m.active !== false));
      setSettings(settingsRes.data);
    } catch { }
  };

  const handleDepositSubmit = async () => {
    const amount = parseFloat(depositAmount);
    if (!amount || amount < settings.min_deposit) {
      toast.error(`Minimum deposit is Rs.${settings.min_deposit}`);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/wallet/deposit`, {
        amount,
        payment_method_id: selectedMethod?.method_id || ""
      });
      setPendingTxnId(data.txn_id);
      setDepositStep("method");
      toast.success("Select payment method");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const handleReceiptUpload = async () => {
    if (!receiptFile || !pendingTxnId) {
      toast.error("Please select a receipt file");
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", receiptFile);
      await axios.post(`${API}/wallet/deposit/${pendingTxnId}/receipt`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Receipt uploaded! Awaiting admin approval.");
      setDepositStep("amount");
      setDepositAmount("");
      setSelectedMethod(null);
      setPendingTxnId(null);
      setReceiptFile(null);
      await fetchData();
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount < settings.min_withdrawal) {
      toast.error(`Minimum withdrawal is Rs.${settings.min_withdrawal}`);
      return;
    }
    if (!accountName || !accountNumber) {
      toast.error("Bank details are required");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/wallet/withdraw`, {
        amount,
        bank_account_name: accountName,
        bank_account_number: accountNumber,
        bank_ifsc: ifsc,
        bank_name: bankName,
        upi_id: upiId
      });
      toast.success("Withdrawal request submitted");
      setWithdrawAmount("");
      setAccountName(""); setAccountNumber(""); setIfsc(""); setBankName(""); setUpiId("");
      await fetchData();
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const loadQrImage = async (path) => {
    try {
      const resp = await axios.get(`${API}/files/${path}`, { responseType: "blob" });
      return URL.createObjectURL(resp.data);
    } catch { return null; }
  };

  const [qrUrls, setQrUrls] = useState({});
  useEffect(() => {
    paymentMethods.forEach(async (m) => {
      if (m.qr_image_path && !qrUrls[m.method_id]) {
        const url = await loadQrImage(m.qr_image_path);
        if (url) setQrUrls(prev => ({ ...prev, [m.method_id]: url }));
      }
    });
  }, [paymentMethods]);

  return (
    <div className="wallet-page" data-testid="wallet-page">
      <h1 className="page-title heading-serif">Wallet</h1>

      <div className="wallet-balance-card" data-testid="wallet-balance">
        <p className="text-sm text-white/60">Account Balance</p>
        <p className="text-3xl font-bold text-white mt-1">&#8377;{(profile?.balance || 0).toFixed(2)}</p>
      </div>

      <div className="wallet-tabs" data-testid="wallet-tabs">
        {["overview", "deposit", "withdraw"].map((s) => (
          <button key={s} className={`wallet-tab ${activeSection === s ? "active" : ""}`}
            onClick={() => { setActiveSection(s); setDepositStep("amount"); }}
            data-testid={`wallet-tab-${s}`}>
            {s === "overview" ? "Analytics" : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {activeSection === "overview" && (
        <div className="wallet-section animate-fade-in" data-testid="wallet-analytics">
          <div className="analytics-grid">
            <div className="analytics-item">
              <ArrowDownLeft size={18} className="text-green-400" />
              <div>
                <p className="analytics-label">Total Deposit</p>
                <p className="analytics-value">&#8377;{analytics?.total_deposit?.toFixed(2) || "0.00"}</p>
              </div>
            </div>
            <div className="analytics-item">
              <ArrowUpRight size={18} className="text-red-400" />
              <div>
                <p className="analytics-label">Total Withdraw</p>
                <p className="analytics-value">&#8377;{analytics?.total_withdraw?.toFixed(2) || "0.00"}</p>
              </div>
            </div>
            <div className="analytics-item">
              <TrendingUp size={18} className="text-[#d4af37]" />
              <div>
                <p className="analytics-label">Commission</p>
                <p className="analytics-value">&#8377;{analytics?.total_commission?.toFixed(2) || "0.00"}</p>
              </div>
            </div>
          </div>
          <h3 className="text-sm font-medium text-slate-400 mt-6 mb-3">Recent Transactions</h3>
          <div className="transactions-list" data-testid="transactions-list">
            {transactions.length === 0 ? (
              <p className="text-center text-slate-500 text-sm py-6">No transactions yet</p>
            ) : transactions.slice(0, 20).map((txn, i) => (
              <div key={i} className="transaction-item" data-testid={`txn-${i}`}>
                <div className="flex items-center gap-3">
                  <div className={`txn-icon ${txn.type === "deposit" ? "deposit" : txn.type === "commission" ? "commission" : "withdraw"}`}>
                    {txn.type === "deposit" ? <ArrowDownLeft size={14} /> : txn.type === "commission" ? <TrendingUp size={14} /> : <ArrowUpRight size={14} />}
                  </div>
                  <div>
                    <p className="text-sm text-white">{txn.description}</p>
                    <p className="text-xs text-slate-500">{txn.status} - {new Date(txn.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <span className={`txn-amount ${txn.type === "withdraw" || txn.type === "product_purchase" ? "negative" : "positive"}`}>
                  {txn.type === "withdraw" || txn.type === "product_purchase" ? "-" : "+"}&#8377;{txn.amount?.toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeSection === "deposit" && (
        <div className="wallet-section animate-fade-in" data-testid="deposit-section">
          {depositStep === "amount" && (
            <div data-testid="deposit-amount-step">
              <h3 className="text-white font-medium mb-2">Deposit Funds</h3>
              <p className="text-xs text-slate-400 mb-4">Minimum: Rs.{settings.min_deposit}</p>
              <label className="auth-label">Amount (Rs.)</label>
              <input type="number" className="auth-input mb-3" placeholder={`Min Rs.${settings.min_deposit}`}
                value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)}
                min={settings.min_deposit} data-testid="deposit-amount-input" />
              <div className="quick-amounts mb-4">
                {[200, 500, 1000, 5000].map((amt) => (
                  <button type="button" key={amt} className="quick-amount-btn" onClick={() => setDepositAmount(String(amt))}>Rs.{amt}</button>
                ))}
              </div>
              <button className="btn-gold" onClick={handleDepositSubmit} disabled={loading} data-testid="deposit-next-btn">
                {loading ? <Loader2 size={18} className="animate-spin mx-auto" /> : "Next - Select Payment Method"}
              </button>
            </div>
          )}

          {depositStep === "method" && (
            <div data-testid="deposit-method-step">
              <button className="back-btn mb-3" onClick={() => setDepositStep("amount")} data-testid="deposit-back-btn">
                <ChevronLeft size={16} className="inline" /> Back
              </button>
              <h3 className="text-white font-medium mb-4">Select Payment Method</h3>
              {paymentMethods.length === 0 ? (
                <p className="text-slate-400 text-sm">No payment methods available. Contact admin.</p>
              ) : (
                <div className="space-y-3">
                  {paymentMethods.map((m, i) => (
                    <button key={i} className={`payment-method-card ${selectedMethod?.method_id === m.method_id ? "selected" : ""}`}
                      onClick={() => { setSelectedMethod(m); setDepositStep("receipt"); }}
                      data-testid={`payment-method-${i}`}>
                      <span className="text-white font-medium">{m.name}</span>
                      {m.upi_id && <span className="text-xs text-slate-400">UPI: {m.upi_id}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {depositStep === "receipt" && selectedMethod && (
            <div data-testid="deposit-receipt-step">
              <button className="back-btn mb-3" onClick={() => setDepositStep("method")} data-testid="receipt-back-btn">
                <ChevronLeft size={16} className="inline" /> Back
              </button>
              <h3 className="text-white font-medium mb-2">Payment Details</h3>
              <p className="text-sm text-slate-400 mb-4">Pay Rs.{depositAmount} using {selectedMethod.name}</p>

              {selectedMethod.upi_id && (
                <div className="upi-display mb-4">
                  <p className="text-xs text-slate-400">UPI ID</p>
                  <p className="text-[#d4af37] font-bold text-lg">{selectedMethod.upi_id}</p>
                </div>
              )}

              {qrUrls[selectedMethod.method_id] && (
                <div className="qr-display mb-4" data-testid="qr-code-display">
                  <img src={qrUrls[selectedMethod.method_id]} alt="QR Code" className="qr-image" />
                </div>
              )}

              <div className="mt-4">
                <label className="auth-label">Upload Payment Receipt</label>
                <div className="file-upload-area" data-testid="receipt-upload-area">
                  <input type="file" accept="image/*" onChange={(e) => setReceiptFile(e.target.files[0])}
                    className="file-input" data-testid="receipt-file-input" />
                  <Upload size={20} className="text-slate-400" />
                  <span className="text-sm text-slate-400">{receiptFile ? receiptFile.name : "Click to upload receipt"}</span>
                </div>
              </div>

              <button className="btn-gold mt-4" onClick={handleReceiptUpload} disabled={loading || !receiptFile}
                data-testid="receipt-submit-btn">
                {loading ? <Loader2 size={18} className="animate-spin mx-auto" /> : "Submit Receipt"}
              </button>
            </div>
          )}
        </div>
      )}

      {activeSection === "withdraw" && (
        <form onSubmit={handleWithdraw} className="wallet-section animate-fade-in" data-testid="withdraw-form">
          <h3 className="text-white font-medium mb-2">Withdraw Funds</h3>
          <p className="text-xs text-slate-400 mb-4">Available: Rs.{(profile?.balance || 0).toFixed(2)} | Min: Rs.{settings.min_withdrawal}</p>

          <div className="space-y-3">
            <div>
              <label className="auth-label">Amount (Rs.)</label>
              <input type="number" className="auth-input" placeholder={`Min Rs.${settings.min_withdrawal}`}
                value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)}
                min={settings.min_withdrawal} data-testid="withdraw-amount-input" />
            </div>
            <div>
              <label className="auth-label">Account Holder Name</label>
              <input type="text" className="auth-input" placeholder="Full name" value={accountName}
                onChange={(e) => setAccountName(e.target.value)} data-testid="withdraw-name-input" />
            </div>
            <div>
              <label className="auth-label">Bank Account Number</label>
              <input type="text" className="auth-input" placeholder="Account number" value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)} data-testid="withdraw-account-input" />
            </div>
            <div>
              <label className="auth-label">IFSC Code</label>
              <input type="text" className="auth-input" placeholder="IFSC code" value={ifsc}
                onChange={(e) => setIfsc(e.target.value)} data-testid="withdraw-ifsc-input" />
            </div>
            <div>
              <label className="auth-label">Bank Name</label>
              <input type="text" className="auth-input" placeholder="Bank name" value={bankName}
                onChange={(e) => setBankName(e.target.value)} data-testid="withdraw-bank-input" />
            </div>
            <div>
              <label className="auth-label">UPI ID (Optional)</label>
              <input type="text" className="auth-input" placeholder="yourname@upi" value={upiId}
                onChange={(e) => setUpiId(e.target.value)} data-testid="withdraw-upi-input" />
            </div>
          </div>

          <button type="submit" className="btn-gold mt-4" disabled={loading} data-testid="withdraw-submit-btn">
            {loading ? <Loader2 size={18} className="animate-spin mx-auto" /> : "Submit Withdrawal"}
          </button>
        </form>
      )}
    </div>
  );
}
